import { Field, Hint, Label, Message } from '@zendeskgarden/react-forms'
import { Body, Close, Header, Modal } from '@zendeskgarden/react-modals'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import LeftArrow from '../../icons/left-arrow.svg?react'
import { PAYEE_TYPE } from '../../models/Payee'
import { PhoneNumber } from '../../models/PhoneNumber'
import { RecordFactory } from '../../models/RecordFactory'
import { POCKETSUITE_USER_ID } from '../../utils/constants'
import { e164ToPhone } from '../../utils/phone'
import { ComponentData } from '../../utils/typing'
import { NavigationBlockerPrompt } from '../NavigationBlockerPrompt'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { PSButton, PSButtonPrimary } from '../app/PSButton'
import { TextFieldV1 } from '../common/fields/TextFieldV1'
import { LearnMoreTeamNumberBody, LearnMoreYourNumberBody } from '../settings/LearnMore'
import { SuccessModal } from '../settings/SuccessModal'

const viewWillAppear = async ({ urlParams, me: user }: PocketComponent<void>) => {
    const phoneNumberPromise = (async () => {
        if (!!urlParams.phoneNumberID) {
            if (!!urlParams.teamMemberID) {
                return undefined
            }
            else {
                return await RecordFactory.getInstance().loadNumber(urlParams.phoneNumberID)
            }
        }
        else {
            if (!!user.sms) {
                return (await RecordFactory.getInstance().loadNumbers()).find(
                    (number) => number.sms === user.sms
                )
            }
            else {
                return undefined
            }
        }
    })()

    const payeesPromise = (async () => {
        if (user.type === 'company') {
            const filteredPayees = (
                await RecordFactory.getInstance().loadPayees(PAYEE_TYPE.PAYEE_TYPE_ANY)
            ).filter(
                (payee) =>
                    payee.id !== POCKETSUITE_USER_ID &&
                    ['employee', 'admin', 'contractor'].includes(payee.role ?? '')
            )
            if (user.sms) {
                return [user, ...filteredPayees]
            }
            else {
                return filteredPayees
            }
        }
        else {
            return [user]
        }
    })()

    const [phoneNumber, payees] = await Promise.all([phoneNumberPromise, payeesPromise])

    return { phoneNumber, payees }
}

const render = ({
    base: { me: user, showProgressBar, hideProgressBar },
    data: { phoneNumber, payees },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    const navigate = usePSNavigate()
    const params = useParams()

    const [phoneNumberID, setPhoneNumberID] = useState('')
    const [sms, setSMS] = useState('')
    const [main, setMain] = useState('')
    const [name, setName] = useState('')

    const [originalMain, setOriginalMain] = useState('')
    const [mainField, setMainField] = useState('')

    const [person, setPerson] = useState<any>()
    const [areaCode, setAreaCode] = useState('')

    const [requestingNewNumber, setRequestingNewNumber] = useState<boolean>(false)
    const [newNumberBody, setNewNumberBody] = useState<string | undefined>(undefined)

    const [isLearnMoreVisible, setIsLearnMoreVisible] = useState(false)

    const newNumber = !!params.teamMemberID
    const teamMemberID = params.teamMemberID
    const teamAccount =
        !!params.phoneNumberID || !!params.teamMemberID || (!!person && person.role !== 'owner')
    const hasBack = !!params.phoneNumberID || !!params.teamMemberID

    useEffect(() => {
        if (!payees.length) return
        if (!teamMemberID && !phoneNumber) return

        if (teamMemberID) {
            const newPerson = payees.filter((u: any) => u.id === teamMemberID)[0]
            setName(newPerson.name ?? '')
            setMain(newPerson?.main?.slice(2)?.replace(/\D/g, '') ?? '')
            setOriginalMain(newPerson?.main?.slice(2)?.replace(/\D/g, '') ?? '')
            setPerson(newPerson)
        }
        else if (phoneNumber) {
            const newSMS = e164ToPhone(phoneNumber?.sms) ?? ''

            setPhoneNumberID(phoneNumber.id ?? '')
            setSMS(newSMS)
            setName(phoneNumber.name ?? '')
            setMain(phoneNumber.main?.slice(2)?.replace(/\D/g, '') ?? '')
            setMainField(phoneNumber.main?.slice(2)?.replace(/\D/g, '') || '')
            setOriginalMain(phoneNumber.main?.slice(2)?.replace(/\D/g, '') ?? '')

            if (!!requestingNewNumber) {
                setNewNumberBody(
                    `This team member’s profile has been updated with a new Premium business line. Their new number is ${newSMS}`
                )
            }

            setPerson(payees.filter((u: any) => u.id === phoneNumber.person)[0])
        }
    }, [payees, phoneNumber, teamMemberID])

    const forwardingValidation = useMemo(() => {
        const USNumber = !main?.match(/^1/)
        const forwardingValid = !main || (main?.length === 10 && USNumber)
        return !forwardingValid ? 'error' : undefined
    }, [])

    useEffect(() => {
        setMain(mainField.replace(/\D/g, ''))
    }, [mainField])

    const hasEdits = areaCode?.length === 3 || originalMain?.replace(/\D/g, '') !== main
    const saveEnabled = hasEdits && !forwardingValidation

    const onSave = async () => {
        showProgressBar()

        let newPhoneNumber: PhoneNumber
        if (phoneNumber === undefined) {
            newPhoneNumber = new PhoneNumber()
            newPhoneNumber.main = main
            newPhoneNumber.person = person.id
            newPhoneNumber.sms = areaCode
        }
        else {
            newPhoneNumber = new PhoneNumber({ id: phoneNumberID })
            newPhoneNumber.main = main
            newPhoneNumber.person = phoneNumber?.person
            newPhoneNumber.name = name
        }

        await RecordFactory.getInstance().sendNumber(newPhoneNumber)

        hideProgressBar()
    }

    const onNewNumberSucessDone = () => {
        navigate(`/settings/account/numbers`)
    }

    return (
        <div className="servicesSettings">
            <div className="header">
                {!!hasBack && (
                    <PSButton
                        style={{ marginLeft: 0, marginRight: 16 }}
                        onClick={() => navigate(-1)}
                    >
                        <LeftArrow />
                    </PSButton>
                )}

                <h1>Number</h1>
                <PSButtonPrimary
                    style={{ height: '40px', marginLeft: 'auto' }}
                    onClick={() => onSave()}
                    disabled={!saveEnabled}
                >
                    Save
                </PSButtonPrimary>
            </div>
            <div className="form">
                <Field className="field">
                    {!!teamAccount && <Label className="label">Team member</Label>}
                    {!teamAccount && <Label className="label">Owner</Label>}
                    <Hint>{name}</Hint>
                </Field>
                {!newNumber && (
                    <Field className="field">
                        <Label className="label">Number</Label>
                        <Hint>{sms}</Hint>
                    </Field>
                )}
                {newNumber && (
                    <Field className="field">
                        <Label className="label">Area code</Label>
                        <Hint>
                            Add the area code of the Premium number you’d like to assign this team
                            member
                        </Hint>
                        <TextFieldV1
                            mask="000"
                            className="mobileNumberInput"
                            placeholder="(123)"
                            onChange={(e) => setAreaCode(e.target.value)}
                        />
                    </Field>
                )}
                <Field className="field">
                    <Label className="label">Forward to</Label>
                    <Hint>Forward all incoming calls on my PocketSuite business line to</Hint>
                    <TextFieldV1
                        mask="000-000-0000"
                        className="mobileNumberInput"
                        placeholder="Forwarding number"
                        validation={forwardingValidation}
                        onChange={(e) => setMainField(e.target.value)}
                    />
                    {!!forwardingValidation && (
                        <Message validation={forwardingValidation}>
                            Only US phone numbers are allowed.
                        </Message>
                    )}
                    <div
                        className="buttonLink brightBlue"
                        style={{ display: 'block', paddingTop: 8 }}
                        onClick={() => setIsLearnMoreVisible(true)}
                    >
                        Learn more &gt;
                    </div>
                </Field>
                {isLearnMoreVisible && (
                    <Modal onClose={() => setIsLearnMoreVisible(false)}>
                        <Header>About this number</Header>
                        <Body>
                            {person.id !== user.id
                                ? LearnMoreTeamNumberBody
                                : LearnMoreYourNumberBody}
                        </Body>
                        <Close aria-label="Close modal" />
                    </Modal>
                )}
                {!!newNumberBody && (
                    <SuccessModal
                        title="Success"
                        body={newNumberBody}
                        buttonText="Done"
                        onClose={() => onNewNumberSucessDone()}
                        onAction={() => onNewNumberSucessDone()}
                    />
                )}
                <NavigationBlockerPrompt when={hasEdits && !newNumberBody} />
            </div>
        </div>
    )
}
export const SetupDashboardPremiumNumberPage = usePSComponent(render, viewWillAppear)
